/**
 * ExportMenu - Analyst Pack download options
 * 
 * Provides export functionality for research reports:
 * - PDF: The Verdict Report (for boss/compliance)
 * - CSV: Financial metrics data table (for Excel models)
 * - JSON: Full audit trail (for technical compliance)
 * - Markdown: Copy-to-clipboard (for Slack/email)
 */

import { useState } from 'react';
import { 
  Download, 
  FileText, 
  FileSpreadsheet,
  FileJson,
  FileCode,
  ChevronDown,
  Loader2,
  ClipboardCheck
} from 'lucide-react';
import { cn } from '@/lib/utils';
import { Tooltip } from '@/components/ui/Tooltip';
import type { ExportFormat } from '@/types/researchModes';

interface ExportMenuProps {
  onExport: (format: ExportFormat) => Promise<void>;
  availableFormats?: ExportFormat[];
  className?: string;
  disabled?: boolean;
  reportTitle?: string;
}

interface ExportOption {
  format: ExportFormat;
  label: string;
  description: string;
  icon: React.ReactNode;
  color: string;
}

const EXPORT_OPTIONS: ExportOption[] = [
  {
    format: 'pdf',
    label: 'PDF Report',
    description: 'Full verdict report for compliance',
    icon: <FileText className="w-4 h-4" />,
    color: 'text-red-400',
  },
  {
    format: 'csv',
    label: 'CSV Data',
    description: 'Financial metrics for Excel',
    icon: <FileSpreadsheet className="w-4 h-4" />,
    color: 'text-green-400',
  },
  {
    format: 'json',
    label: 'JSON Audit Trail',
    description: 'Full debate log for compliance',
    icon: <FileJson className="w-4 h-4" />,
    color: 'text-amber-400',
  },
  {
    format: 'markdown',
    label: 'Copy Summary',
    description: 'Markdown for Slack/email',
    icon: <FileCode className="w-4 h-4" />,
    color: 'text-blue-400',
  },
];

export function ExportMenu({
  onExport,
  availableFormats = ['pdf', 'csv', 'json', 'markdown'],
  className,
  disabled = false,
  reportTitle,
}: ExportMenuProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [exportingFormat, setExportingFormat] = useState<ExportFormat | null>(null);
  const [copiedFormat, setCopiedFormat] = useState<ExportFormat | null>(null);

  const filteredOptions = EXPORT_OPTIONS.filter(opt => availableFormats.includes(opt.format));

  const handleExport = async (format: ExportFormat) => {
    if (disabled || exportingFormat) return;
    
    setExportingFormat(format);
    try {
      await onExport(format);
      if (format === 'markdown') {
        setCopiedFormat(format);
        setTimeout(() => setCopiedFormat(null), 2000);
      }
    } finally {
      setExportingFormat(null);
    }
  };

  return (
    <div className={cn('relative', className)}>
      {/* Main button */}
      <Tooltip content="Download research in multiple formats" position="top">
        <button
          type="button"
          onClick={() => !disabled && setIsOpen(!isOpen)}
          disabled={disabled}
          className={cn(
            'flex items-center gap-2 px-4 py-2 rounded-lg border transition-all',
            'bg-surface border-border text-text-primary',
            disabled
              ? 'opacity-50 cursor-not-allowed'
              : 'hover:bg-surface-light hover:border-border-light'
          )}
        >
          <Download className="w-4 h-4" />
          <span className="text-sm font-medium">Export</span>
          <ChevronDown className={cn('w-4 h-4 transition-transform', isOpen && 'rotate-180')} />
        </button>
      </Tooltip>

      {/* Dropdown menu - expands upward since it's in footer */}
      {isOpen && !disabled && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />
          <div className="absolute bottom-full left-0 mb-2 z-50 w-80 min-w-[320px] max-w-[calc(100vw-2rem)] bg-surface border border-border rounded-xl shadow-xl overflow-hidden">
            {/* Header */}
            <div className="px-4 py-3 border-b border-border">
              <p className="text-xs font-medium text-text-secondary uppercase tracking-wider">
                Analyst Pack
              </p>
              {reportTitle && (
                <p className="text-sm text-text-primary mt-1 truncate">{reportTitle}</p>
              )}
            </div>

            {/* Options */}
            <div className="py-1">
              {filteredOptions.map((option) => {
                const isExporting = exportingFormat === option.format;
                const isCopied = copiedFormat === option.format;
                
                return (
                  <button
                    key={option.format}
                    type="button"
                    onClick={() => handleExport(option.format)}
                    disabled={isExporting}
                    className={cn(
                      'w-full flex items-center gap-3 px-4 py-3 text-left transition-colors',
                      'hover:bg-surface-light/50',
                      isExporting && 'opacity-50'
                    )}
                  >
                    <div className={option.color}>
                      {isExporting ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : isCopied ? (
                        <ClipboardCheck className="w-4 h-4 text-green-400" />
                      ) : (
                        option.icon
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium text-text-primary">
                          {option.label}
                        </span>
                        {isCopied && (
                          <span className="text-xs text-green-400">Copied!</span>
                        )}
                      </div>
                      <p className="text-xs text-text-muted">{option.description}</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default ExportMenu;

